/**
 * Learning-platform content testing subpage copy for both languages.
 *
 * Extracted verbatim from the former monolithic translations file; the
 * /content-testing, /content-testing-eu and /lerninhalte-testen pages read it.
 */
import type { Language } from './LanguageContext';

export interface ContentTestingTranslations {
    navLabel: string;
    metaTitle: string;
    metaDescription: string;
    backToHome: string;
    readInOtherLanguage: string;
    seeEuroPrices: string;
    seeUsdPrices: string;
    hero: {
      headline: string;
      subheadline: string;
      body: string;
      pricing: string;
      cta: string;
      offer: string;
    };
    pillarsTitle: string;
    pillarsSubtitle: string;
    pillars: { title: string; description: string }[];
    processTitle: string;
    processIntro: string;
    processSteps: { title: string; description: string }[];
    processRepeat: string;
    caseTitle: string;
    caseBody: string;
    caseWebLabel: string;
    caseOrgLabel: string;
    applyEyebrow: string;
    applyTitle: string;
    applyLead: string;
    applyPricing: string;
    applyCta: string;
    applyFinePrint: string;
    form: {
      title: string;
      name: string;
      email: string;
      organisation: string;
      role: string;
      rolePlaceholder: string;
      roles: { value: string; label: string }[];
      platformUrl: string;
      access: string;
      accessOptions: { value: string; label: string }[];
      scope: string;
      scopePlaceholder: string;
      submit: string;
      successTitle: string;
      successBody: string;
      requiredNote: string;
    };
    chat: {
      greeting: string;
      quickReplies: string[];
    };
}

export const contentTestingTranslations: Record<Language, ContentTestingTranslations> = {
  en: {
      navLabel: "Content testing",
      metaTitle: "Learning platform testing in 24 hours | TamerinTECH",
      metaDescription:
        "AI agents test your learning platform in a browser and check the content, including AI-generated material. From {fromPrice} for up to 5,000 content questions. Free standard setup. Larger volumes quoted individually.",
      backToHome: "Back to homepage",
      readInOtherLanguage: "Auf Deutsch lesen",
      seeEuroPrices: "Prices in euro",
      seeUsdPrices: "Prices in US dollars",
      hero: {
        headline: "We test your learning platform in 24 hours.",
        subheadline:
          "Find confusing questions, incorrect answers and broken learning experiences before your learners do.",
        body: "Our AI agents test your platform through a browser, following the same steps as your learners. We check both the learning experience and the content, including material generated with AI.",
        pricing: "From {fromPrice}. Up to 5,000 content questions. Free standard setup.",
        cta: "Book a scan from {fromPrice}",
        offer: "First report within 24 hours after scope, payment and access are confirmed."
      },
      pillarsTitle: "Not just whether the button works. Whether the learning works.",
      pillarsSubtitle: "We test the journeys your learners take and the content they meet along the way.",
      pillars: [
        {
          title: "Browser-based testing",
          description:
            "We use a dedicated test account to open lessons, complete exercises, submit answers and check the feedback. We look for problems in the agreed user journeys, from missing content to interactions that prevent learners from completing a task."
        },
        {
          title: "Educational content checks",
          description:
            "Is the question clear? Is there enough information to answer it? Is the expected answer correct? Does the explanation make sense? We check human-written and AI-generated content for issues that need your team’s attention."
        },
        {
          title: "API and offline-file testing",
          description:
            "Prefer to provide content directly? We can also run checks through a supported API connection or work with exported content files, without logging into your platform."
        }
      ],
      processTitle: "Simple to start. Clear results.",
      processIntro:
        "Share your platform details and the content or learner journeys you want checked. We confirm the scope and arrange access through a test account, API or supported files.",
      processSteps: [
        {
          title: "Agree the scope",
          description:
            "You tell us the platform, the journeys or content to cover, and how we should get access. We confirm what the first scan includes before anything runs."
        },
        {
          title: "Automated checks, human review",
          description:
            "Our automated checks run, with human review of key findings. You receive a report showing what was tested, what needs attention and why, with examples, reproduction steps where relevant and suggested fixes."
        },
        {
          title: "Fix, then re-scan",
          description:
            "After making changes, we can scan again to check whether the issues were resolved. Repeat scans and volumes above 5,000 questions are quoted individually."
        }
      ],
      processRepeat:
        "After making changes, we can scan again. Repeat scans and volumes above 5,000 questions are quoted individually.",
      caseTitle: "Built on hands-on experience with iRainbow.",
      caseBody:
        "Our experience includes automated testing of iRainbow’s web learning platform, covering interactive exercises and educational content, including AI-generated material. We check what learners actually see, whether questions can be answered and whether the answers and explanations are clear and correct.",
      caseWebLabel: "iRainbow Web",
      caseOrgLabel: "iRainbow Education",
      applyEyebrow: "Apply now",
      applyTitle: "Give your content team a second pair of eyes.",
      applyLead:
        "Tell us about your platform and the journeys or content you want checked. We confirm the scope, arrange access and send the first report within 24 hours after payment and working access are in place.",
      applyPricing: "From {fromPrice} for up to 5,000 content questions. Free standard setup.",
      applyCta: "Book a scan from {fromPrice}",
      applyFinePrint:
        "The starting price covers up to 5,000 content questions in an agreed scope. Larger volumes, repeat scans after corrections and custom integrations are quoted individually. The first report is delivered within 24 hours after the scope, payment and working access or supported files are confirmed. Prices exclude VAT where applicable.",
      form: {
        title: "Book a first scan",
        name: "Name",
        email: "Work email",
        organisation: "Organisation",
        role: "Role",
        rolePlaceholder: "Select a role",
        roles: [
          { value: "content", label: "Content or curriculum" },
          { value: "qa", label: "QA or learning experience" },
          { value: "product", label: "Product" },
          { value: "founder", label: "Founder or leadership" },
          { value: "other", label: "Other" }
        ],
        platformUrl: "Platform URL",
        access: "How should we access the content?",
        accessOptions: [
          { value: "test-account", label: "Dedicated test account in the browser" },
          { value: "api", label: "Supported API connection" },
          { value: "files", label: "Exported content files" }
        ],
        scope: "What should we check?",
        scopePlaceholder: "Lessons, subjects, or learner journeys you want in the first scan",
        submit: "Send booking request",
        successTitle: "Your booking request is ready to send.",
        successBody:
          "Your email app should open with the details. If it does not, write to info@tamerin.tech. You can also finish the booking in the chat on this page.",
        requiredNote: "Name, email and organisation are required."
      },
      chat: {
        greeting:
          "Hi! I can help you book a learning-platform scan. Prices start from {fromPrice} for up to 5,000 content questions, and standard setup is free. Larger volumes get an individual quote. Ask me how it works, what we check, or start an application.",
        quickReplies: [
          "How does a scan work?",
          "What’s included from {fromPrice}?",
          "How do I apply?",
          "Something else"
        ]
      }
  },
  de: {
      navLabel: "Inhalte testen",
      metaTitle: "Lernplattform-Test in 24 Stunden | TamerinTECH",
      metaDescription:
        "KI-Agenten testen Ihre Lernplattform im Browser und prüfen die Inhalte, einschließlich KI-generierter Materialien. Ab {fromPrice} für bis zu 5.000 Inhaltsfragen. Kostenloses Standard-Setup. Größere Umfänge individuell.",
      backToHome: "Zurück zur Startseite",
      readInOtherLanguage: "Read in English",
      seeEuroPrices: "Preise in Euro",
      seeUsdPrices: "Preise in US-Dollar",
      hero: {
        headline: "Wir testen Ihre Lernplattform in 24 Stunden.",
        subheadline:
          "Finden Sie unklare Fragen, falsche Antworten und kaputte Lernerfahrungen, bevor Ihre Lernenden das tun.",
        body: "Unsere KI-Agenten testen Ihre Plattform im Browser und folgen denselben Schritten wie Ihre Lernenden. Wir prüfen die Lernerfahrung und die Inhalte, einschließlich KI-generierter Materialien.",
        pricing: "Ab {fromPrice}. Bis zu 5.000 Inhaltsfragen. Kostenloses Standard-Setup.",
        cta: "Scan ab {fromPrice} buchen",
        offer: "Erster Bericht innerhalb von 24 Stunden, sobald Umfang, Zahlung und Zugang bestätigt sind."
      },
      pillarsTitle: "Nicht nur, ob der Button funktioniert. Ob das Lernen funktioniert.",
      pillarsSubtitle: "Wir prüfen die Wege, die Ihre Lernenden gehen, und die Inhalte, denen sie dabei begegnen.",
      pillars: [
        {
          title: "Tests im Browser",
          description:
            "Wir nutzen ein eigenes Testkonto, öffnen Lektionen, bearbeiten Übungen, senden Antworten und prüfen das Feedback. Wir suchen nach Problemen in den vereinbarten Nutzerreisen – von fehlenden Inhalten bis zu Interaktionen, die Lernende daran hindern, eine Aufgabe abzuschließen."
        },
        {
          title: "Prüfung der Lerninhalte",
          description:
            "Ist die Frage klar? Gibt es genug Informationen, um sie zu beantworten? Ist die erwartete Antwort richtig? Ist die Erklärung nachvollziehbar? Wir prüfen menschlich geschriebene und KI-generierte Inhalte auf Punkte, die Ihr Team angehen sollte."
        },
        {
          title: "API- und Offline-Datei-Tests",
          description:
            "Möchten Sie Inhalte direkt bereitstellen? Wir können die Prüfung auch über eine unterstützte API-Verbindung oder mit exportierten Inhaltsdateien durchführen, ohne uns in Ihre Plattform einzuloggen."
        }
      ],
      processTitle: "Einfacher Start. Klare Ergebnisse.",
      processIntro:
        "Teilen Sie uns Ihre Plattformdaten und die Inhalte oder Lernendenreisen mit, die geprüft werden sollen. Wir bestätigen den Umfang und klären den Zugang über ein Testkonto, eine API oder unterstützte Dateien.",
      processSteps: [
        {
          title: "Umfang vereinbaren",
          description:
            "Sie nennen uns die Plattform, die Reisen oder Inhalte und den Zugangsweg. Wir bestätigen, was der erste Scan umfasst, bevor etwas läuft."
        },
        {
          title: "Automatisierte Prüfung, menschliche Durchsicht",
          description:
            "Unsere automatisierten Prüfungen laufen, wichtige Funde werden menschlich gegengeprüft. Sie erhalten einen Bericht mit dem, was getestet wurde, was Aufmerksamkeit braucht und warum – mit Beispielen, Reproduktionsschritten wo relevant und Korrekturvorschlägen."
        },
        {
          title: "Korrigieren, dann erneut scannen",
          description:
            "Nach Änderungen können wir erneut scannen, um zu prüfen, ob die Probleme behoben sind. Wiederholungsscans und Umfänge über 5.000 Fragen werden individuell angeboten."
        }
      ],
      processRepeat:
        "Nach Änderungen können wir erneut scannen. Wiederholungsscans und Umfänge über 5.000 Fragen werden individuell angeboten.",
      caseTitle: "Aufgebaut auf praktischer Erfahrung mit iRainbow.",
      caseBody:
        "Unsere Erfahrung umfasst automatisierte Tests der Web-Lernplattform von iRainbow, einschließlich interaktiver Übungen und Lerninhalte, auch KI-generierter Materialien. Wir prüfen, was Lernende tatsächlich sehen, ob Fragen beantwortbar sind und ob Antworten und Erklärungen klar und korrekt sind.",
      caseWebLabel: "iRainbow Web",
      caseOrgLabel: "iRainbow Education",
      applyEyebrow: "Jetzt anfragen",
      applyTitle: "Geben Sie Ihrem Content-Team ein zweites Paar Augen.",
      applyLead:
        "Erzählen Sie uns von Ihrer Plattform und den Reisen oder Inhalten, die geprüft werden sollen. Wir bestätigen den Umfang, klären den Zugang und liefern den ersten Bericht innerhalb von 24 Stunden, nachdem Zahlung und funktionierender Zugang stehen.",
      applyPricing: "Ab {fromPrice} für bis zu 5.000 Inhaltsfragen. Kostenloses Standard-Setup.",
      applyCta: "Scan ab {fromPrice} buchen",
      applyFinePrint:
        "Der Einstiegspreis gilt für bis zu 5.000 Inhaltsfragen in einem vereinbarten Umfang. Größere Umfänge, Wiederholungsscans nach Korrekturen und individuelle Integrationen werden separat angeboten. Der erste Bericht wird innerhalb von 24 Stunden geliefert, nachdem Umfang, Zahlung und funktionierender Zugang oder unterstützte Dateien bestätigt sind. Preise zzgl. MwSt., soweit anwendbar.",
      form: {
        title: "Ersten Scan buchen",
        name: "Name",
        email: "Geschäftliche E-Mail",
        organisation: "Organisation",
        role: "Rolle",
        rolePlaceholder: "Rolle wählen",
        roles: [
          { value: "content", label: "Content oder Curriculum" },
          { value: "qa", label: "QA oder Lernerfahrung" },
          { value: "product", label: "Produkt" },
          { value: "founder", label: "Gründung oder Leitung" },
          { value: "other", label: "Sonstiges" }
        ],
        platformUrl: "Plattform-URL",
        access: "Wie sollen wir auf die Inhalte zugreifen?",
        accessOptions: [
          { value: "test-account", label: "Eigenes Testkonto im Browser" },
          { value: "api", label: "Unterstützte API-Verbindung" },
          { value: "files", label: "Exportierte Inhaltsdateien" }
        ],
        scope: "Was sollen wir prüfen?",
        scopePlaceholder: "Lektionen, Fächer oder Lernendenreisen für den ersten Scan",
        submit: "Buchungsanfrage senden",
        successTitle: "Ihre Buchungsanfrage ist bereit zum Senden.",
        successBody:
          "Ihr E-Mail-Programm sollte sich mit den Angaben öffnen. Falls nicht, schreiben Sie an info@tamerin.tech. Sie können die Buchung auch im Chat auf dieser Seite abschließen.",
        requiredNote: "Name, E-Mail und Organisation sind Pflichtfelder."
      },
      chat: {
        greeting:
          "Hallo! Ich helfe Ihnen, einen Scan Ihrer Lernplattform zu buchen. Die Preise beginnen bei {fromPrice} für bis zu 5.000 Inhaltsfragen, das Standard-Setup ist kostenlos. Größere Umfänge erhalten ein individuelles Angebot. Fragen Sie nach dem Ablauf, dem Leistungsumfang oder starten Sie eine Anfrage.",
        quickReplies: [
          "Wie läuft ein Scan ab?",
          "Was gilt ab {fromPrice}?",
          "Wie kann ich buchen?",
          "Etwas anderes"
        ]
      }
  }
};
